import { Component, type ReactNode, type ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global error boundary that catches JavaScript errors anywhere in the app
 * and displays a user-friendly fallback UI with recovery options.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRefresh = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/home';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <div
            className="w-full max-w-md rounded-lg bg-white p-6 shadow-elevated"
            role="alert"
            aria-live="assertive"
          >
            <div className="flex flex-col items-center text-center">
              {/* Error icon */}
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-danger/20">
                <svg
                  className="h-8 w-8 text-danger"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              <h1 className="text-xl font-semibold text-editorial-black">
                Something went wrong
              </h1>

              <p className="mt-2 text-sm text-gray-500">
                We encountered an unexpected error. Please try refreshing the
                page or go back to the home screen.
              </p>

              {/* Error details in development */}
              {import.meta.env.DEV && this.state.error && (
                <details className="mt-4 w-full rounded bg-gray-100 p-2 text-left">
                  <summary className="cursor-pointer text-xs text-gray-500">
                    Error details (dev only)
                  </summary>
                  <pre className="mt-2 overflow-auto text-xs text-danger">
                    {this.state.error.message}
                    {'\n'}
                    {this.state.error.stack}
                  </pre>
                </details>
              )}

              {/* Action buttons */}
              <div className="mt-6 flex w-full flex-col gap-3">
                <button
                  onClick={this.handleRefresh}
                  className="flex h-btn-lg min-h-btn-lg w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 font-medium text-white transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Refresh page
                </button>

                <button
                  onClick={this.handleGoHome}
                  className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg border-2 border-gray-300 px-6 py-3 font-medium text-editorial-black transition-colors hover:border-primary hover:bg-primary-light focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                  Go to home
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
